import { useFirebase } from 'contexts/FirebaseContext';
import dayjs from 'dayjs';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { useUser } from 'hooks/useUser';
import { LocalTrack } from 'models/LocalTrack';
import { Requester } from 'models/Requester';
import { Track } from 'models/Track';
import {
  TrackRequest,
  TrackRequestMessage,
  TrackRequestPriority,
  TrackRequestType,
  TrackRequesterPriorityValue,
} from 'models/TrackRequest';
import { useState } from 'react';
import { cleanArtistTitle } from 'utils/cleanArtistTitle';
import { isElectron } from 'utils/isElectron';
import { requestHash } from 'utils/requestHash';
import { useLocalDatabase } from './useLocalDatabase';
import { useTrackMapping } from './useTrackMapping';
const sortRequests = (requestsList: TrackRequest[]): TrackRequest[] => {
  return [...requestsList].sort((a, b) => {
    const aTime = a.date ? new Date(a.date).getTime() : 0;
    const bTime = b.date ? new Date(b.date).getTime() : 0;

    // If dates are different (more than 1 second apart), sort by date descending
    if (Math.abs(aTime - bTime) > 1000) {
      return bTime - aTime;
    }

    // Within the same second (e.g. batch imported Spotify playlist), sort by index ascending
    if (a.index !== undefined && b.index !== undefined) {
      return a.index - b.index;
    }

    if (a.index !== undefined) return -1;
    if (b.index !== undefined) return 1;

    return bTime - aTime;
  });
};

export function useRequests(userId?: string) {
  const [requests, setRequests] = useState<TrackRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const { localDatabase } = useLocalDatabase();
  const db = useFirebase().db;
  const { getMappedTrack, getMappingAutoMatchThresholdValue, getFindMatchThreshold } =
    useTrackMapping();
  const { requesterId, preferences } = useUser();

  const findLocalTracks = async (request: TrackRequest<Track>, maxDistance = 1.0, limit = 20) => {
    if (isElectron() && request.type === TrackRequestType.Track) {
      // search with original parameters
      //const result = await localDatabase?.findTracks(request.value.title, request.value.artist,null,null,maxDistance,limit);
      const result = await localDatabase?.findTracks({
        title: request.value.title,
        artist: request.value.artist,
        maxDistance,
        limit,
      });

      if (result) {
        return result;
      }

      // research with cleaned parameters
      const { artist, title } = cleanArtistTitle(request.value);
      //return localDatabase?.findTracks(title, artist,null,null,null);
      return await localDatabase?.findTracks({
        title: title,
        artist: artist,
        maxDistance,
        limit,
      });
    }
  };

  const processRequestMapping = async (
    request: TrackRequest,
    localDatabase: any,
    getMappedTrack: any
  ): Promise<TrackRequest> => {
    if (isElectron()) {
      if (request.localId != null) {
        request.localTrack = await localDatabase?.getTrackById(request.localId);
      } else {
        const threshold = getMappingAutoMatchThresholdValue(preferences.autoMatchThreshold);
        const findMatchThreshold = getFindMatchThreshold();
        const localTrack = request.skipAutoMap ? null : await getMappedTrack(request);

        if (localTrack) {
          request.localId = localTrack?.id;
          request.localTrack = localTrack;
        } else {
          request.foundLocalTracks = await findLocalTracks(
            request as TrackRequest<Track>,
            findMatchThreshold
          );

          if (null != request.foundLocalTracks) {
            var trackRequest = request as TrackRequest<Track>;

            const logRequestName =
              request.type === TrackRequestType.Track
                ? `${trackRequest.value.artist} - ${trackRequest.value.title}`
                : `${request.value}`;

            const distance = request?.foundLocalTracks[0]?.distance;

            if (!request.skipAutoMap && distance && distance <= threshold) {
              console.log(
                `${logRequestName}: ${request.foundLocalTracks.length} found, best distance: ${distance}, threshold: ${threshold}`
              );
              request.localTrack = request.foundLocalTracks[0];
              request.localId = request.foundLocalTracks[0].id;
              request.autoMapped = true;
            } else {
              request.localTrack = undefined;
              request.localId = undefined;
              request.autoMapped = false;

              if (distance) {
                console.log(`${logRequestName}: Distance ${distance}, threshold: ${threshold}`);
              } else {
                console.log(`${logRequestName}: Distance is undefined`);
              }
            }
          }
        }
      }
    }

    return request;
  };

  const fetchRequests = async (eventId: string) => {
    // console.trace('fetchRequests called');
    if (!userId) {
      return [];
    }

    setIsLoadingRequests(true);

    const requestsCollection = collection(db, `user/${userId}/events/${eventId}/requests`);
    const requestsSnapshot = await getDocs(requestsCollection);

    const res = await Promise.all(
      requestsSnapshot.docs.map(async (requestDoc) => {
        // Process mapping
        const processedRequest = await processRequestMapping(
          requestDoc.data() as TrackRequest,
          localDatabase,
          getMappedTrack
        );

        const request = {
          ...processedRequest,
          id: requestDoc.id,
        };

        // Get requesters
        const requestersCollection = collection(
          db,
          `user/${userId}/events/${eventId}/requests/${request.id}/requesters`
        );

        const requestersSnapshot = await getDocs(requestersCollection);
        request.requesters = requestersSnapshot.docs.map((requesterDoc) => {
          const requester = requesterDoc.data() as Requester;

          return {
            ...requester,
            id: requesterDoc.id,
          };
        });

        // Get messages
        const messagesCollection = collection(
          db,
          `user/${userId}/events/${eventId}/requests/${request.id}/messages`
        );

        const messagesSnapshot = await getDocs(messagesCollection);
        request.messages = messagesSnapshot.docs.map((messageDoc) => {
          const message = messageDoc.data() as TrackRequestMessage;

          return {
            ...message,
            id: messageDoc.id,
          };
        });

        // Get priorities
        const prioritiesCollection = collection(
          db,
          `user/${userId}/events/${eventId}/requests/${request.id}/priorities`
        );

        const prioritiesSnapshot = await getDocs(prioritiesCollection);
        request.priorities = prioritiesSnapshot.docs.map((priorityDoc) => {
          const priority = priorityDoc.data() as TrackRequestPriority;

          return {
            ...priority,
            id: priorityDoc.id,
          };
        });

        return request;
      })
    );

    // console.log(
    //   `fetchRequests: fetching from path: user/${userId}/events/${eventId}/requests (Project: ${db.app.options.projectId})`
    // );
    // console.log('fetchRequests: total requests fetched from DB:', res.length);
    // res.forEach((r, i) => {
    //   console.log(
    //     `request ${i}: id=${r.id}, title=${
    //       r.type === TrackRequestType.Track ? (r.value as Track).title : r.value
    //     }, requesters=${JSON.stringify(r.requesters)}, isDeleted=${r.isDeleted}`
    //   );
    // });

    // const filteredRequests = res.filter((r) => r.requesters?.length);
    // console.log(
    //   'fetchRequests: requests after filtering empty requesters:',
    //   filteredRequests.length
    // );

    const sortedRequests = sortRequests(res);
    setIsLoadingRequests(false);
    setRequests(sortedRequests);

    return sortedRequests;
  };

  const updateRequests = async (eventId: string, updateRequests: TrackRequest[]) => {
    const updatePromises = updateRequests.map(async (updatedRequest) => {
      const requestId = updatedRequest.id;
      const docData: any = { ...updatedRequest };
      delete docData.id;
      delete docData.localTrack;
      delete docData.foundLocalTracks;

      // Delete fields that are undefined to avoid storing them in Firestore
      Object.keys(docData).forEach((key) => {
        if (docData[key] === undefined) {
          docData[key] = deleteField();
        }
      });

      if (userId && requestId) {
        await setDoc(doc(db, `user/${userId}/events/${eventId}/requests`, requestId), docData, {
          merge: true,
        });
      }
    });

    await Promise.all(updatePromises);

    setRequests((prevRequests) =>
      sortRequests(
        prevRequests.map(
          (req) => updateRequests.find((updatedReq) => updatedReq.id === req.id) || req
        )
      )
    );
  };

  // const updateRequests = async (eventId: string, updateRequests: TrackRequest[]) => {
  //   const updatePromises = updateRequests.map(async (updatedRequest) => {
  //     const requestId = updatedRequest.id;

  //     // Clone data to delete fields without reference
  //     const docData = { ...updatedRequest };
  //     delete docData.id;
  //     delete docData.localTrack;
  //     delete docData.foundLocalTracks;

  //     if (userId && requestId) {
  //       await setDoc(doc(db, `user/${userId}/events/${eventId}/requests`, requestId), docData, {
  //         merge: true,
  //       });
  //     }
  //   });

  //   await Promise.all(updatePromises);

  //   setRequests((prevRequests) =>
  //     prevRequests.map(
  //       (req) => updateRequests.find((updatedReq) => updatedReq.id === req.id) || req
  //     )
  //   );
  // };

  const addAnonymousRequests = async (eventId: string, requestTracks: TrackRequest[]) => {
    const requestsCollection = collection(db, `/user/${userId}/events/${eventId}/requests`);
    const importDate = dayjs().toISOString();

    // Sort requestTracks by index ascending before inserting sequentially
    const sortedTracks = [...requestTracks].sort((a, b) => {
      const idx1 = a.index !== undefined ? a.index : 0;
      const idx2 = b.index !== undefined ? b.index : 0;
      return idx1 - idx2;
    });

    const updatedRequests: TrackRequest[] = [];

    // Perform database entries sequentially in order of index
    for (const requestTrack of sortedTracks) {
      const q = query(requestsCollection, where('hash', '==', requestTrack.hash));
      const requestSnapshot = await getDocs(q);

      const requesterData: Requester = {
        date: importDate,
      };

      if (requestSnapshot.empty) {
        requestTrack.date = requestTrack.date || importDate;
        const newRequestDoc = await addDoc(requestsCollection, requestTrack);
        requestTrack.id = newRequestDoc.id;
      } else {
        requestTrack.id = requestSnapshot.docs[0].id;
      }

      await setDoc(
        doc(
          db,
          `/user/${userId}/events/${eventId}/requests/${requestTrack.id}/requesters`,
          requesterId
        ),
        requesterData
      );

      requesterData.id = requesterId;

      requestTrack.requesters = [...(requestTrack.requesters || []), requesterData];
      updatedRequests.push(requestTrack);
    }

    setRequests((prevRequests) => {
      const existingMap = new Map(prevRequests.map((req) => [req.hash, req]));
      updatedRequests.forEach((req) => existingMap.set(req.hash, req));
      return sortRequests(Array.from(existingMap.values()));
    });
  };

  const removeAnonymousRequest = async (eventId: string, request: TrackRequest) => {
    let requestId = request.id;

    // Try to find the request ID if it is not set
    if (!requestId) {
      const requestsCollection = collection(db, `/user/${userId}/events/${eventId}/requests`);
      const q = query(requestsCollection, where('hash', '==', request.hash));
      const requestSnapshot = await getDocs(q);

      if (!requestSnapshot.empty) {
        requestId = requestSnapshot.docs[0].id;
      }
    }

    // Can not remove requests without an ID
    if (!requestId) {
      return;
    }

    const foundRequest = requests.find(
      (request) =>
        request.id === requestId && request.requesters?.some(({ id }) => id === requesterId)
    );

    if (foundRequest) {
      foundRequest.requesters = foundRequest.requesters?.filter(({ id }) => id !== requesterId);

      const requesterDocRef = doc(
        db,
        `/user/${userId}/events/${eventId}/requests/${requestId}/requesters/${requesterId}`
      );

      try {
        await deleteDoc(requesterDocRef);

        setRequests((prevRequests) =>
          sortRequests(
            prevRequests.map((req) => (foundRequest.hash === req.hash ? foundRequest : req))
          )
        );

        await removeRequestPriority(eventId, foundRequest);
      } catch (error) {
        console.error('Error removing requester document: ', error);
      }
    }
  };

  const addRequestMapping = async (
    eventId: string,
    request: TrackRequest,
    localTrack: LocalTrack,
    rememberSelection: boolean
  ) => {
    const mappingId =
      request.type == TrackRequestType.Track
        ? await requestHash((request.value as Track).title, (request.value as Track).artist)
        : await requestHash(request.value as string);

    const mappingRef = localDatabase?.getMapping(mappingId);

    if (rememberSelection) {
      if (mappingRef === undefined) {
        localDatabase?.addMapping(mappingId, localTrack.id, localTrack.path);
      } else {
        localDatabase?.updateMapping(mappingId, localTrack.id, localTrack.path);
      }
    }

    updateRequests(eventId, [
      {
        ...request,
        localId: localTrack.id,
        localTrack: localTrack,
        autoMapped: false,
        skipAutoMap: false,
      },
    ]);
  };

  const removeRequestMapping = async (
    eventId: string,
    request: TrackRequest,
    rememberSelection: boolean
  ) => {
    const mappingId =
      request.type == TrackRequestType.Track
        ? await requestHash((request.value as Track).title, (request.value as Track).artist)
        : await requestHash(request.value as string);

    const mappingRef = localDatabase?.getMapping(mappingId);

    if (rememberSelection && mappingRef !== undefined) {
      localDatabase?.removeMapping(mappingId);
    }

    updateRequests(eventId, [
      {
        ...request,
        localId: undefined,
        localTrack: undefined,
        autoMapped: false,
        skipAutoMap: true,
      },
    ]);
  };

  const updateRequestMessage = async (message: string, eventId: string, request: TrackRequest) => {
    if (!userId || !request.id || !requesterId) {
      return;
    }

    const messageData: TrackRequestMessage = {
      value: message,
      date: new Date().toISOString(),
    };

    await setDoc(
      doc(db, `/user/${userId}/events/${eventId}/requests/${request.id}/messages`, requesterId),
      messageData
    );

    setRequests((prevRequests) =>
      prevRequests.map((req) => {
        if (req.hash !== request.hash) {
          return req;
        }

        const existingMessages = req.messages || [];
        const filteredMessages = existingMessages.filter((message) => message.id !== requesterId);

        return {
          ...req,
          messages: [...filteredMessages, { ...messageData, id: requesterId }],
        };
      })
    );
  };

  const removeRequestMessage = async (eventId: string, request: TrackRequest) => {
    if (!userId || !request.id || !requesterId) {
      return;
    }

    const messageDocRef = doc(
      db,
      `/user/${userId}/events/${eventId}/requests/${request.id}/messages/${requesterId}`
    );

    try {
      await deleteDoc(messageDocRef);

      setRequests((prevRequests) => {
        return prevRequests.map((req) => {
          if (req.hash !== request.hash) {
            return req;
          }

          const existingMessages = req.messages || [];
          const filteredMessages = existingMessages.filter(
            (priority) => priority.id !== requesterId
          );

          return {
            ...req,
            messages: filteredMessages,
          };
        });
      });
    } catch (error) {
      console.error('Error removing priority document: ', error);
    }
  };

  const updateRequestPriority = async (
    requestPriorityValue: TrackRequesterPriorityValue,
    eventId: string,
    request: TrackRequest
  ) => {
    if (!userId || !request.id || !requesterId) {
      return;
    }

    const priorityData: TrackRequestPriority = {
      value: requestPriorityValue,
    };

    await setDoc(
      doc(db, `/user/${userId}/events/${eventId}/requests/${request.id}/priorities`, requesterId),
      priorityData
    );

    if (
      requestPriorityValue === TrackRequesterPriorityValue.High ||
      requestPriorityValue === TrackRequesterPriorityValue.Normal
    ) {
      await addAnonymousRequests(eventId, [request]);
    } else if (requestPriorityValue === TrackRequesterPriorityValue.Low) {
      await removeAnonymousRequest(eventId, request);
    }

    setRequests((prevRequests) =>
      prevRequests.map((req) => {
        if (req.hash !== request.hash) {
          return req;
        }

        const existingPriorities = req.priorities || [];
        const filteredPriorities = existingPriorities.filter(
          (priority) => priority.id !== requesterId
        );

        return {
          ...req,
          priorities: [...filteredPriorities, { ...priorityData, id: requesterId }],
        };
      })
    );
  };

  const removeRequestPriority = async (eventId: string, request: TrackRequest) => {
    if (!userId || !request.id || !requesterId) {
      return;
    }

    const priorityDocRef = doc(
      db,
      `/user/${userId}/events/${eventId}/requests/${request.id}/priorities/${requesterId}`
    );

    try {
      await deleteDoc(priorityDocRef);

      setRequests((prevRequests) => {
        return prevRequests.map((req) => {
          if (req.hash !== request.hash) {
            return req;
          }

          const existingPriorities = req.priorities || [];
          const filteredPriorities = existingPriorities.filter(
            (priority) => priority.id !== requesterId
          );

          return {
            ...req,
            priorities: filteredPriorities,
          };
        });
      });
    } catch (error) {
      console.error('Error removing priority document: ', error);
    }
  };

  return {
    requests,
    validRequests: requests.filter((r) => !r.isDeleted),
    deletedRequests: requests.filter((r) => r.isDeleted === true),
    isLoadingRequests,
    fetchRequests,
    updateRequests,
    addAnonymousRequests,
    removeAnonymousRequest,
    findLocalTracks,
    processRequestMapping,
    addRequestMapping,
    removeRequestMapping,
    updateRequestMessage,
    removeRequestMessage,
    updateRequestPriority,
    removeRequestPriority,
  };
}
